import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import FilesTable from './FilesTable';
import useAgentCallReviewsFiles from '../hooks/useAgentCallReviewsFiles';
import useClampPageToRange from 'hooks/useClampPageToRange';

/**
 * body of an expanded roster row: one agent's reports, loaded on first expand.
 * cached rows stay on screen while they revalidate, and a failed refresh keeps
 * them visible with an inline note instead of blanking the table.
 * @param agentId - zoho agent id of the expanded row
 * @param page - 1-indexed page for this agent, owned by the page component
 * @param onPageChange - called with the page to move to
 * @param agentName - agent of the expanded row, forwarded to the preview title
 * @param onOpenFile - called with the row whose pdf should be opened
 */
export default function AgentFilesPanel({
  agentId,
  agentName,
  page,
  onPageChange,
  onOpenFile
}) {
  const {
    files,
    totalPages,
    isLoading,
    isRefreshing,
    isRetrying,
    hasError,
    refetch
  } = useAgentCallReviewsFiles(agentId, page);

  const handlePageChange = useCallback(
    nextPage => onPageChange(agentId, nextPage),
    [agentId, onPageChange]
  );

  useClampPageToRange({
    page,
    totalPages,
    itemCount: files.length,
    onPageChange: handlePageChange
  });

  if (isLoading) {
    return (
      <p className='flex items-center justify-center gap-2 py-6 text-[length:--font-size-body] text-grey'>
        <Loader2 className='size-4 animate-spin' aria-hidden />
        Loading reports…
      </p>
    );
  }

  if (hasError && files.length === 0) {
    return (
      <div className='flex flex-col items-center gap-2 py-6'>
        <p className='text-[length:--font-size-body] text-grey'>
          Could not load this agent&apos;s reports.
        </p>
        <Button
          variant='default'
          outlined
          size='xsmall'
          rounded='medium'
          text='Retry'
          onClick={refetch}
        />
      </div>
    );
  }

  return (
    <FilesTable
      compact
      showPager
      items={files}
      page={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      onOpenFile={onOpenFile}
      agentName={agentName}
      refreshing={isRefreshing}
      retrying={isRetrying}
      error={
        hasError ? 'Could not refresh — showing the last loaded data.' : null
      }
    />
  );
}

AgentFilesPanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  agentName: PropTypes.string,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func.isRequired
};
